/**
 * Walking a list one item at a time — the strip, and the arrows in the gutter.
 *
 * Pure view. It is handed a label and two callbacks and draws them; it knows
 * nothing about routes, ids, or what is being walked. Everything that differs
 * between a teacher stepping through an attempt's questions and an admin
 * stepping through a page of operation runs lives in the view model each
 * builds, which is why this can be one component in two apps.
 *
 * The arrows exist because these pages run long. The strip scrolls away with
 * the top of the page, so by the time a reader has read enough to move on, the
 * control to move on is off-screen. The arrows follow them down instead.
 *
 * Positioning is STICKY, not fixed: the page sits in a flex layout whose
 * sidebar collapses, so a viewport-anchored button cannot know where the gutter
 * is, while a zero-height sticky box inherits the column's own edges. That does
 * mean this must render INSIDE the page column it should hang off — see
 * `ItemNav.css` for the offsets, which the host tunes to its own padding.
 */
import './ItemNav.css';

export interface ItemNavViewModel {
    /** Where the reader is, in words: "Question 8 of 13", "Run 3 of 20". */
    label: string;
    /** Absent at the first item — there is nowhere back to go. */
    onPrev?: () => void;
    /** Absent at the last item. */
    onNext?: () => void;
    /** Hover text for the gutter arrows, e.g. "Previous question". */
    prevTitle?: string;
    nextTitle?: string;
}

export function ItemNavView({ label, onPrev, onNext, prevTitle, nextTitle }: ItemNavViewModel) {
    return (
        <>
            <div className="item-nav">
                <button type="button" className="item-nav-step" disabled={!onPrev} onClick={onPrev}>
                    &larr; Prev
                </button>
                <span className="item-nav-label">{label}</span>
                <button type="button" className="item-nav-step" disabled={!onNext} onClick={onNext}>
                    Next &rarr;
                </button>
            </div>

            {/* Absent rather than disabled at the ends: the strip above still
                shows a greyed Prev/Next, so nothing is hidden about why.
                `aria-hidden` + `tabIndex={-1}` because these are a second way to
                press the buttons above, not a second pair of controls — a
                screen reader or a tab sequence should meet each move once. */}
            <div className="item-nav-rails" aria-hidden="true">
                {onPrev && (
                    <button
                        type="button"
                        className="item-nav-rail item-nav-rail--prev"
                        onClick={onPrev}
                        title={prevTitle}
                        tabIndex={-1}
                    >
                        &larr;
                    </button>
                )}
                {onNext && (
                    <button
                        type="button"
                        className="item-nav-rail item-nav-rail--next"
                        onClick={onNext}
                        title={nextTitle}
                        tabIndex={-1}
                    >
                        &rarr;
                    </button>
                )}
            </div>
        </>
    );
}
